import React, { useState, useEffect, useMemo } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import ImageModal from "../../../../../library/custom/imageModal/imageModal";
import "./viewImages.scss";
import { compressImage, getFileInfo } from "../../../../../utils/helper";
import commonImages from "../../../../../assets";

// Callers pass urls straight from API responses, so anything that is not a
// usable url string is dropped rather than reaching getFileInfo or an <img>.
const isUsableUrl = (url) => typeof url === "string" && url.length > 0;

const ViewImages = ({
  imageSize,
  image,
  singleImage = false,
  singleImagespath,
  multiImage = true,
  singleImagesid,
  showName = true,
}) => {
  const [clickedImg, setClickedImg] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [convertedImages, setConvertedImages] = useState({});
  const [failedImages, setFailedImages] = useState({});
  const [loadedImages, setLoadedImages] = useState({});

  const images = useMemo(() => {
    if (!multiImage || !Array.isArray(image)) return [];
    return image.filter(isUsableUrl);
  }, [image, multiImage]);

  // Stable string representation so the effect only reruns when the urls change
  const imageKey = useMemo(
    () => (images.length > 0 ? images.join("|") : null),
    [images],
  );

  useEffect(() => {
    if (images.length === 0) return;

    let cancelled = false;
    const CONCURRENCY = 3;

    // Every image is fetched and re-encoded to a small jpeg before display:
    // phone-camera originals run several MB each and a report can carry dozens,
    // so rendering them at source size is what stalls the page. A single
    // block (e.g. one question's corrective photos) can hold most of a
    // report's images, so a handful run side by side rather than one at a
    // time — enough for network and worker to overlap, capped so a long list
    // still can't spike memory or spin up unbounded workers at once. Each
    // thumbnail still appears as soon as its own conversion is ready.
    (async () => {
      let nextIndex = 0;
      const runNext = async () => {
        while (!cancelled) {
          const i = nextIndex++;
          if (i >= images.length) return;
          const url = images[i];
          const converted = await compressImage(url);
          if (cancelled) return;
          if (converted) {
            setConvertedImages((prev) => ({ ...prev, [url]: converted }));
          } else {
            setFailedImages((prev) => ({ ...prev, [url]: true }));
          }
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, images.length) }, runNext),
      );
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageKey]);

  // Every url keeps a tile. Until the browser has actually painted the image
  // the tile shows the shared no-image placeholder, plus a spinner while there
  // is still something to wait for. A url that fails conversion or fails to
  // decode keeps the placeholder and drops the spinner.
  const renderableImages = useMemo(
    () =>
      images.map((url) => {
        const hasFailed = !!failedImages[url];
        return {
          url,
          src: hasFailed ? null : convertedImages[url],
          hasFailed,
          isLoaded: !hasFailed && !!loadedImages[url],
          fileName: getFileInfo(url)?.fileName,
        };
      }),
    [images, convertedImages, failedImages, loadedImages],
  );

  // Modal navigation only walks images that are actually viewable, so arrows
  // never land on a placeholder tile.
  const viewableImages = useMemo(
    () => renderableImages.filter((item) => item.isLoaded),
    [renderableImages],
  );

  const handleClick = (item) => {
    const index = viewableImages.findIndex(
      (viewable) => viewable.url === item.url,
    );
    setCurrentIndex(index);
    setClickedImg(item.src);
  };

  const handleImageLoad = (url) => {
    setLoadedImages((prev) => ({ ...prev, [url]: true }));
  };

  const handleImageError = (url) => {
    setFailedImages((prev) => ({ ...prev, [url]: true }));
  };

  const handleRotationRight = () => {
    if (viewableImages.length === 0 || currentIndex === null) return;

    const newIndex = (currentIndex + 1) % viewableImages.length;
    setCurrentIndex(newIndex);
    setClickedImg(viewableImages[newIndex]?.src);
  };

  const handleRotationLeft = () => {
    if (viewableImages.length === 0 || currentIndex === null) return;

    const newIndex =
      (currentIndex - 1 + viewableImages.length) % viewableImages.length;
    setCurrentIndex(newIndex);
    setClickedImg(viewableImages[newIndex]?.src);
  };

  return (
    <>
      {multiImage && renderableImages.length > 0 ? (
        <div className="viewImageBox">
          <div
            className={
              imageSize?.size === "small" ? "viewList small" : "viewList large"
            }
          >
            {renderableImages.map((item, i) => (
              <div className="listItem" key={`${item.url}-${i}`}>
                {!item.isLoaded && (
                  <img
                    className="noImagePlaceholder"
                    src={commonImages?.noimage}
                    alt="Preview unavailable"
                  />
                )}
                {!item.hasFailed && !item.isLoaded && (
                  <CircularProgress className="imageLoader" size={20} />
                )}
                {item.src && (
                  // Kept mounted but transparent and out of flow while loading
                  // so the browser still decodes it behind the placeholder.
                  <img
                    onClick={() => item.isLoaded && handleClick(item)}
                    onLoad={() => handleImageLoad(item.url)}
                    onError={() => handleImageError(item.url)}
                    src={item.src}
                    alt={item.fileName}
                    loading="lazy"
                    decoding="async"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      ...(item.isLoaded
                        ? {}
                        : {
                            position: "absolute",
                            top: 0,
                            left: 0,
                            opacity: 0,
                            pointerEvents: "none",
                          }),
                    }}
                  />
                )}
                {showName && <div className="imageName">{item.fileName}</div>}
              </div>
            ))}

            {clickedImg && (
              <ImageModal
                clickedImg={clickedImg}
                handleRotationRight={handleRotationRight}
                setClickedImg={setClickedImg}
                handleRotationLeft={handleRotationLeft}
              />
            )}
          </div>
        </div>
      ) : null}
      {singleImage && (
        <div className="viewImageBox">
          <div
            className={
              imageSize?.size === "small" ? "viewList small" : "viewList large"
            }
          >
            <div className="listItem">
              <img
                onClick={() => {
                  setCurrentIndex(null);
                  setClickedImg(singleImagespath);
                }}
                src={singleImagespath}
                alt=""
                loading="lazy"
                decoding="async"
              />
            </div>
            {clickedImg && (
              <ImageModal
                clickedImg={clickedImg}
                handleRotationRight={handleRotationRight}
                setClickedImg={setClickedImg}
                handleRotationLeft={handleRotationLeft}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ViewImages;
